import { useRef, useState } from 'react';
import SortOptionsSheet, { AnySortingKey, SortOption } from '../../sort-options-sheet/sort-options-sheet';
import { SortButton } from '../sort-button/sort-button';

interface SortControlProps<T extends AnySortingKey> {
    componentClass?: string;
    selectedSortType?: T;
    sortOptions?: SortOption<T>[];
    onSort?: (type: T) => void;
}

/**
 * A sort trigger button paired with its options sheet. Owns the button ref and the
 * sheet's visibility/position state internally so callers only need to supply the
 * sorting data itself - no plumbing for a ref, `isSortVisible`, or a position to thread
 * between the trigger and the sheet.
 */
export function SortControl<T extends AnySortingKey>({ componentClass, selectedSortType, sortOptions, onSort }: SortControlProps<T>) {
    const [visible, setVisible] = useState(false);
    const [position, setPosition] = useState({ left: 0, top: 0 });
    const buttonRef = useRef<HTMLButtonElement>(null);

    return (
        <>
            <SortButton
                ref={buttonRef}
                componentClass={componentClass}
                isSortVisible={visible}
                setSortPosition={setPosition}
                setSortVisible={setVisible}
                selectedSortType={selectedSortType}
                sortOptions={sortOptions}
            />
            <SortOptionsSheet
                leftPosition={position.left}
                topPosition={position.top}
                selectedAction={selectedSortType}
                onAction={(actionType) => {
                    setVisible(false);
                    onSort?.(actionType);
                }}
                visible={visible}
                setVisible={setVisible}
                options={sortOptions}
                triggerRef={buttonRef}
            />
        </>
    );
}

export default SortControl;
